'use client'

import { Button } from '@/components/ui/Button'
import { createCheckout } from '@/services/stripe/client'
import { sendGTMEvent } from '@next/third-parties/google'
import { useState } from 'react'

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL

export default function UpgradeButton({
  userData,
  className,
  size = 'md',
  fullWidth = true,
  children = 'Upgrade',
  trackingLocation = 'unknown',
}) {
  const [loading, setLoading] = useState(false)

  const handleUpgrade = async () => {
    setLoading(true)

    // ✅ Track: upgrade_button_clicked
    sendGTMEvent({
      event: 'upgrade_button_clicked',
      tracking_location: trackingLocation,
    })

    try {
      const customerId = await ensureCustomerId(userData)

      if (!customerId) {
        throw new Error('Failed to create customer')
      }

      const session = await createCheckout({
        customer_id: customerId,
        cancelUrl: `${SITE_URL}/subscribe`,
        successUrl: `${SITE_URL}/subscribed`,
      })

      if (session?.url) {
        window.location.href = session.url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (error) {
      console.error('Subscription error:', error)
      alert('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      onClick={handleUpgrade}
      loading={loading}
      disabled={loading}
      size={size}
      fullWidth={fullWidth}
      className={className}
    >
      {loading ? 'Loading...' : children}
    </Button>
  )
}

// Helper function to ensure user has a valid customer_id
async function ensureCustomerId(userData) {
  const existingCustomerId = userData?.userProfile?.customer_id

  // Has customer_id - verify it's valid in Stripe
  if (existingCustomerId) {
    const isValid = await verifyCustomer(existingCustomerId)

    if (isValid) {
      console.log('Customer verified, using existing customer_id')
      return existingCustomerId
    }

    console.warn(
      'Customer ID in database does not exist in Stripe, creating new customer'
    )
  }

  // No customer_id or invalid - create new customer
  console.log('Creating new customer')
  return await createNewCustomer(userData)
}

// Verify customer exists in Stripe
async function verifyCustomer(customerId) {
  try {
    const response = await fetch('/api/stripe/customer/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_id: customerId }),
    })

    if (!response.ok) {
      return false
    }

    const data = await response.json()
    return data.exists === true
  } catch (error) {
    console.error('Error verifying customer:', error)
    return false
  }
}

// Create new customer in Stripe and update database
async function createNewCustomer(userData) {
  try {
    const response = await fetch('/api/stripe/customer/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name:
          userData?.userProfile?.display_name ||
          userData?.user?.email?.split('@')[0] ||
          'Pano Reader',
        email: userData?.user?.email,
        user_id: userData?.user?.id,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to create customer')
    }

    const customerData = await response.json()
    return customerData.id
  } catch (error) {
    console.error('Error creating customer:', error)
    return null
  }
}
